<template>
    <div>
        <DxForm ref="formInstance" :form-data="model">
            <DxTabbedItem>
                <DxTab :title="$t('sucesos.popup_guardar_suceso.nombre_pestana_datos')">
                    <DxGroupItem>
                        <DxColCountByScreen :xs="6" :sm="6" :md="6" :lg="6" />

                        <DxItem data-field="IdSucesoTipo" editor-type="dxSelectBox" :editor-options="editorTipoOptions"
                            :col-span="6" :visible="false">
                            <DxLabel :text="$t('sucesos.popup_guardar_suceso.id_suceso_tipo')" />
                            <DxRequiredRule
                                :message="$t('sucesos.popup_guardar_suceso.mensaje_validacion_obligatorio')" />
                        </DxItem>

                        <DxItem data-field="IdSucesoCategoriaTipo" editor-type="dxSelectBox"
                            :editor-options="editorCategoriaTipoOptions" :col-span="5">
                            <DxLabel :text="$t('sucesos.popup_guardar_suceso.id_suceso_categoria_tipo')" />
                            <DxRequiredRule
                                :message="$t('sucesos.popup_guardar_suceso.mensaje_validacion_obligatorio')" />
                        </DxItem>

                        <DxItem data-field="Wiki" editor-type="dxCheckBox" :col-span="1">
                            <DxLabel :text="$t('sucesos.popup_guardar_suceso.wiki')" />
                        </DxItem>

                        <DxItem data-field="Comentarios" editor-type="dxTextArea" :editor-options="txtAreaOptions"
                            :col-span="6">
                            <DxLabel :text="$t('sucesos.popup_guardar_suceso.comentarios')" />
                            <DxRequiredRule
                                :message="$t('oscilos.popup_guardar_oscilo.mensaje_validacion_obligatorio')" />
                        </DxItem>
                    </DxGroupItem>
                </DxTab>
                <DxTab :title="$t('sucesos.popup_guardar_suceso.nombre_pestana_adjuntos')">
                    <DxSimpleItem template="templateFileUploader">
                    </DxSimpleItem>
                </DxTab>
            </DxTabbedItem>
            <template #templateFileUploader>
                <MultipleFileUploader :max-files="4" v-model:ficheros="ficheros"
                    :supported-extensions="AcceptedMimeTypes"
                    :textForAddAttachmentLabel="$t('sucesos.popup_guardar_suceso.texto_para_etiqueta_anadir_adjunto')"
                    :textForAddAttachmentButton="$t('sucesos.popup_guardar_suceso.texto_para_boton_anadir_adjunto')"
                    :textForNonDownloadableFiles="$t('sucesos.popup_guardar_suceso.texto_para_ficheros_no_descargables')"
                    @fileDownloadRequested="onFileDownloadRequested">
                </MultipleFileUploader>
            </template>
        </DxForm>
    </div>
</template>

<script setup lang="ts">
import { isEqual, omit } from "lodash";
import { DxForm } from "devextreme-vue";
import { useIntl } from '@services/i18n';
import type { SaveSucesoModalModel } from "./Model";
import { useApi } from '@/.shared/.api/integration';
import type { AdjuntoSuceso, EditSucesoModel, ReferenciaFicheroModel } from "@/.shared/.api";
import { AcceptedMimeTypes } from "@/model/mime-types";
import { DxRequiredRule } from "devextreme-vue/data-grid";
import { useModalService } from "@/.shared/services/modal-service";
import { useModalBuilder } from '@/.shared/helpers/modal-building-helper';
import { computed, getCurrentInstance, onMounted, ref, toRaw, } from 'vue';
import { validateDxFormAsync } from '@/.shared/helpers/dxvalidation-helper';
import { toastSuccess, toastError } from '@/.shared/services/notification-service';
import { DxGroupItem, DxItem, DxLabel, DxColCountByScreen, DxAsyncRule, DxTabbedItem, DxTab, DxSimpleItem } from "devextreme-vue/form";
import MultipleFileUploader from "@/.shared/components/file-uploading/MultipleFileUploader.vue";

const props = defineProps<{ sucesoProp: SaveSucesoModalModel }>();
const editing = computed(() => (props.sucesoProp != null) && (props.sucesoProp.Id != null) && (props.sucesoProp.Id != 0));
const model = computed(() => (editing.value) ? { ...props.sucesoProp } : {} as SaveSucesoModalModel);
// const model = computed(() => {
//     return { ...{ Adjuntos: [] as AdjuntoSuceso[], AdjuntosExistentes: [] as ReferenciaFicheroModel[] }, ...props.sucesoProp };
// });
const initialModel = computed(() => (editing.value) ? { ...toRaw(props.sucesoProp) } : {} as SaveSucesoModalModel);
const idSuceso = computed(() => (!editing.value) ? undefined : (model.value as SaveSucesoModalModel).Id);

const api = useApi();
const intl = useIntl();
const modalBuilder = useModalBuilder();
const modalService = useModalService();

const ficheros = ref<Ficheros>({});
const formInstance = ref<DxForm>();
const txtAreaOptions = {
    autoResizeEnabled: true,
    minHeight: 100,
    maxHeight: 200
};
const editorTipoOptions = ref<any>(
    {
        displayExpr: "Nombre",
        valueExpr: "Id",
        searchEnabled: "true"
        // onValueChanged: tipoComunicadoChanged
    });
const editorCategoriaTipoOptions = ref<any>(
    {
        displayExpr: "Nombre",
        valueExpr: "Id",
        searchEnabled: "true"
        // onValueChanged: tipoComunicadoChanged
    });

onMounted(async () => {
    modalBuilder.setContext(getCurrentInstance()!);

    ficheros.value = model.value.AdjuntosExistentes.select(x => {
        return { file: new File([], x.Nombre), id: x.Id.toString(), fileName: x.Nombre, isRemote: true, status: "unchanged" } as Fichero
    }).toObject((x) => x.id) as Ficheros;

    let botones = modalBuilder.standardButtons.addOkCancel();
    botones.okButton.update((b) => {
        b.text = intl.i18n.t("sucesos.popup_guardar_suceso.boton_anadir_suceso")
        b.onClick = async () => {
            await guardarSuceso();
        }
    });

    botones.cancelButton.update((b) => {
        b.onClick = async () => {
            const title = (editing.value) ? intl.i18n.t("sucesos.popup_guardar_suceso.titulo_confirmacion_cancelacion_edicion") : intl.i18n.t("sucesos.popup_guardar_suceso.titulo_confirmacion_cancelacion_creacion");
            const message = (editing.value) ? intl.i18n.t("sucesos.popup_guardar_suceso.mensaje_confirmacion_cancelacion_edicion") : intl.i18n.t("sucesos.popup_guardar_suceso.mensaje_confirmacion_cancelacion_creacion");
            if (haSidoFormularioModificado()) {
                const result = await modalService.messageBox(message, title, 'yesNo', 'warning');
                if (result === "no")
                    return;
            }
            modalBuilder.close(false);
        }
    });

    await rellenarTiposDxSelectBox();
    await rellenarCategoriaTiposDxSelectBox();
});

async function rellenarTiposDxSelectBox(): Promise<void> {
    const data = await api.callStandardApi("sucesoTipoGetTodos", { showLoading: true });
    editorTipoOptions.value = { ...editorTipoOptions, dataSource: data };
}

async function rellenarCategoriaTiposDxSelectBox(): Promise<void> {
    const data = await api.callStandardApi("sucesoCategoriaTipoGetTodos", { showLoading: true });
    editorCategoriaTipoOptions.value = { ...editorCategoriaTipoOptions, dataSource: data };
}

function haSidoFormularioModificado(): boolean {
    return !(isEqual(toRaw(model.value), toRaw(initialModel.value)) && Object.getOwnPropertyNames(ficheros.value).all(x => ficheros.value[x].status === "unchanged"));
}

async function onFileDownloadRequested(fichero: Fichero) {
    const apiCallChain = await api.prepareStandardApi("sucesoAttachmentDownload")(model.value.Id!, Number.parseInt(fichero.id));
    const href = `${apiCallChain.axiosInstance.defaults.baseURL!}${apiCallChain.axiosConfiguration.url}`;
    window.location.href = href;
}

async function guardarSuceso() {
    const result = await validateDxFormAsync(formInstance.value!.instance!);
    if (result.isValid) {
        if (editing.value) {
            // Clonar el objeto model.value sin las propiedades entre []; omit es de lodash
            const modalModel = omit(model.value, ["AdjuntosExistentes"]) as SaveSucesoModalModel;
            await setAdjuntos(modalModel, ficheros.value!);
            await api.callStandardApi("sucesoEdit", { showLoading: true }, modalModel as EditSucesoModel);
            toastSuccess(intl.i18n.t('sucesos.popup_guardar_suceso.mensaje_suceso_editado'));
        }
        modalBuilder.close(true);
    } else {
        toastError(intl.i18n.t('sucesos.popup_guardar_suceso.mensaje_validacion_no_superada'));
    }
}

async function setAdjuntos(modalModel: SaveSucesoModalModel, ficheros: Ficheros): Promise<void> {
    for (const key of Object.getOwnPropertyNames(ficheros)) {
        const fichero = ficheros[key];
        if (fichero.status === "new" || fichero.status === "modified") {
            const uploadId = await api.callStandardApi("sucesoAttachmentUpload", {}, idSuceso.value!, fichero.fileName!, fichero.file!);
            modalModel.Adjuntos.push({ Id: 0, NombreFichero: fichero.fileName!, GuidSubida: uploadId });
        }
        else if (fichero.status === "unchanged") {
            modalModel.Adjuntos.push({ Id: parseInt(key), NombreFichero: null, GuidSubida: null });
        }
    }
}

</script>

<style scoped lang="scss">
@import "@/styles/_vars.scss";
@import "@/styles/figma.scss";
@import "@/styles/_themevars.scss";

:deep(.dx-layout-manager .dx-tabpanel .dx-multiview-item-content) {
    padding: 12px;
}
</style>
